"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import Image from "next/image"
import { Menu, X, Phone, Mail } from "lucide-react"
import { cn } from "@/lib/utils"

type DropdownItem = {
  name: string
  href: string
  description?: string
}

type NavItem = {
  name: string
  href: string
  dropdownKey?: string
}

const navigationLinks: NavItem[] = [
  { name: "About", href: "/about", dropdownKey: "about" },
  { name: "Services", href: "/services", dropdownKey: "services" },
  { name: "Resources", href: "/resources" },
  { name: "FAQ", href: "/faq" },
  { name: "Testimonials", href: "/testimonials" },
  { name: "Contact", href: "/contact" },
]

const dropdowns: Record<string, DropdownItem[]> = {
  about: [
    {
      name: "About Meredith",
      href: "/about",
      description: "Meet the practitioner behind M.O. Hypnosis",
    },
    {
      name: "Credentials & Training",
      href: "/credentials",
      description: "Certification, education & clinical approach",
    },
  ],
  services: [
    { name: "Anxiety & Stress", href: "/services/anxiety" },
    { name: "IEMT", href: "/services/iemt" },
    { name: "Trauma & PTSD", href: "/services/trauma" },
    { name: "Self-Esteem", href: "/services/self-esteem" },
    { name: "Grief & Loss", href: "/services/grief" },
    { name: "Addiction Recovery", href: "/services/addiction" },
    { name: "Relationship Challenges", href: "/services/relationships" },
    { name: "Fears & Phobias", href: "/services/fears-phobias" },
    { name: "Women's Wellness", href: "/services/womens-wellness" },
    { name: "View All Services", href: "/services" },
  ],
}

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState<string | null>(null)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <div className="sticky top-0 z-50">
      {/* Top contact bar — in flow above the main nav */}
      <div className="hidden lg:block bg-[#faf6f0]/90 backdrop-blur-md border-b border-[#ede0d4]">
        <div className="max-w-7xl mx-auto px-6 py-2 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6 text-foreground/75">
            <a
              href="tel:+16198768898"
              className="flex items-center gap-2 hover:text-primary transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              (619) 876-8898
            </a>
            <a
              href="mailto:meredith@mohypnosis.com"
              className="flex items-center gap-2 hover:text-primary transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              meredith@mohypnosis.com
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-foreground/60 text-xs tracking-wide uppercase">
              Serving San Diego &amp; Online
            </span>
          </div>
        </div>
      </div>

      {/* Main navigation header — in flow, sticky to top on scroll */}
      <header
        className={cn(
          "left-0 right-0 transition-all duration-500",
          isScrolled
            ? "bg-white shadow-[0_4px_32px_-8px_hsl(270_25%_40%/0.10)] py-2"
            : "bg-white py-3 lg:bg-white/65 lg:backdrop-blur-lg",
          isScrolled && "lg:bg-white/95 lg:backdrop-blur-xl"
        )}
      >
        <div className="max-w-7xl mx-auto px-6">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3">
            <Image
              src="/images/meredith-osetek-logo.png"
              alt="MO Hypnosis - Meredith Osetek"
              width={120}
              height={40}
              className="h-10 w-auto"
              priority
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigationLinks.map((link) => {
              const items = link.dropdownKey ? dropdowns[link.dropdownKey] : null
              return (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => link.dropdownKey && setOpenDropdown(link.dropdownKey)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  {items ? (
                    <>
                      <button
                        className={cn(
                          "px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200",
                          "hover:bg-primary/5 hover:text-primary",
                          "flex items-center gap-1"
                        )}
                        aria-expanded={openDropdown === link.dropdownKey}
                      >
                        {link.name}
                        <svg
                          className={cn(
                            "w-4 h-4 transition-transform",
                            openDropdown === link.dropdownKey && "rotate-180"
                          )}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {/* Dropdown */}
                      <div
                        className={cn(
                          "absolute top-full left-1/2 -translate-x-1/2 pt-2 transition-all duration-200",
                          openDropdown === link.dropdownKey
                            ? "opacity-100 translate-y-0 pointer-events-auto"
                            : "opacity-0 -translate-y-2 pointer-events-none"
                        )}
                      >
                        <div
                          className={cn(
                            "bg-white rounded-2xl shadow-lifted p-3 border border-border/50",
                            items.some((i) => i.description) ? "min-w-[270px]" : "min-w-[220px]"
                          )}
                        >
                          {items.map((item) => (
                            <Link
                              key={item.name}
                              href={item.href}
                              className="block px-4 py-2.5 rounded-lg hover:bg-primary/5 hover:text-primary transition-colors"
                            >
                              {item.description ? (
                                <span>
                                  <span className="block text-sm font-medium text-foreground">
                                    {item.name}
                                  </span>
                                  <span className="block text-xs text-muted-foreground mt-0.5">
                                    {item.description}
                                  </span>
                                </span>
                              ) : (
                                <span className="block text-sm">{item.name}</span>
                              )}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <Link
                      href={link.href}
                      className={cn(
                        "px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200",
                        "hover:bg-primary/5 hover:text-primary"
                      )}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              )
            })}

            <Link
              href="/contact"
              className="ml-4 inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-sm transition-all duration-200 hover:bg-primary/90 hover:shadow-md"
            >
              Book A FREE Consultation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <div
        className={cn(
          "lg:hidden absolute top-full left-0 right-0 max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain bg-white border-b border-border/60 shadow-[0_12px_32px_-12px_hsl(270_25%_40%/0.18)] transition-all duration-300 origin-top",
          isMobileMenuOpen
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-4 pointer-events-none"
        )}
      >
        <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
          <Link
            href="/contact"
            onClick={() => setIsMobileMenuOpen(false)}
            className="block w-full rounded-full bg-primary px-5 py-3.5 text-center text-sm font-semibold text-primary-foreground shadow-sm transition-all duration-200 hover:bg-primary/90 hover:shadow-md"
          >
            Book A FREE Consultation
          </Link>
          {navigationLinks.map((link) => {
            const items = link.dropdownKey ? dropdowns[link.dropdownKey] : null
            const isMobileDropdownOpen = !!items && mobileOpenDropdown === link.dropdownKey
            if (items) {
              return (
                <div key={link.name} className="border-b border-border/30">
                  <div className="flex items-center justify-between">
                    <Link
                      href={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block py-3 text-lg font-medium hover:text-primary transition-colors"
                    >
                      {link.name}
                    </Link>
                    <button
                      type="button"
                      onClick={() =>
                        setMobileOpenDropdown(isMobileDropdownOpen ? null : link.dropdownKey!)
                      }
                      className="p-2 rounded-lg hover:bg-muted transition-colors"
                      aria-expanded={isMobileDropdownOpen}
                      aria-label={`Toggle ${link.name} menu`}
                    >
                      <svg
                        className={cn(
                          "w-4 h-4 transition-transform",
                          isMobileDropdownOpen && "rotate-180"
                        )}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>
                  <div
                    className={cn(
                      "pl-4 space-y-1 overflow-hidden transition-all duration-300",
                      isMobileDropdownOpen ? "max-h-96 pb-2" : "max-h-0"
                    )}
                  >
                    {items.map((item) => (
                      <Link
                        key={item.name}
                        href={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block py-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )
            }
            return (
              <div key={link.name} className="border-b border-border/30">
                <Link
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block py-3 text-lg font-medium hover:text-primary transition-colors"
                >
                  {link.name}
                </Link>
              </div>
            )
          })}
        </div>
      </div>
    </header>
    </div>
  )
}
